import { useMemo } from "react";
import { PERMISSIONS } from "@/constants/permissions";
import { usePermission } from "@/hooks/usePermission";

const MENU_PERMISSION_ACCESS: Record<string, string[]> = {
  organizations: [PERMISSIONS.ORGANIZATION],
  admins: [
    PERMISSIONS.USERS,
    PERMISSIONS.ACL_GET_PERMISSIONS_OF_ROLE,
    PERMISSIONS.ACL_CREATE_CUSTOM_ROLE,
    PERMISSIONS.ACL_UPDATE_CUSTOM_ROLE,
  ],
  billing: [PERMISSIONS.BILLING_READ],
};

// Ordered list of all top-level sections. The first match becomes the default.
const DEFAULT_MENU_ORDER: { id: string; path: string }[] = [
  { id: "organizations", path: "/organizations" },
  { id: "admins", path: "/admins" },
  { id: "billing", path: "/billing" },
];

// Route path prefix → menu id used for route-level permission checks.
// Multiple routes can map to the same menu id (e.g. admins sub-routes).
export const ROUTE_PERMISSION_MAP: Record<string, string> = {
  "/organizations": "organizations",
  "/admins": "admins",
  "/it-admins": "admins",
  "/acl": "admins",
  "/billing": "billing",
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseSidebarPermissionsReturn {
  /**
   * Pass your full menuItemsBase array — get back only what this role can see.
   * Sub-menus are included automatically when their parent id is allowed.
   */
  filterMenuItems: <T extends { id: string; subMenus?: { id: string }[] }>(
    items: T[],
  ) => T[];
  /** Check a single menu item by its `id` field. */
  isItemAccessible: (menuId: string) => boolean;
  /** True for SUPERADMIN / OWNER — bypasses all restrictions. */
  isPrivileged: boolean;
  /** Raw role name from the API, e.g. "BILLING" | "SUPERADMIN" */
  roleName: string;
  /** Path of the first section this role can access (for default redirect). */
  firstAccessiblePath: string;
}

const useSidebarPermissions = (): UseSidebarPermissionsReturn => {
  const { hasAnyPermission } = usePermission();

  const allowedIds = useMemo<Set<string>>(() => {
    return new Set(
      Object.entries(MENU_PERMISSION_ACCESS)
        .filter(([, requiredPermissions]) =>
          hasAnyPermission(requiredPermissions),
        )
        .map(([menuId]) => menuId),
    );
  }, [hasAnyPermission]);

  const firstAccessiblePath = useMemo<string>(() => {
    const match = DEFAULT_MENU_ORDER.find((m) => allowedIds.has(m.id));
    return match?.path ?? "/404";
  }, [allowedIds]);

  const isItemAccessible = useMemo(
    () =>
      (menuId: string): boolean => {
        return allowedIds.has(menuId);
      },
    [allowedIds],
  );

  const filterMenuItems = useMemo(
    () =>
      <T extends { id: string; subMenus?: { id: string }[] }>(
        items: T[],
      ): T[] => {
        return items
          .filter((item) => allowedIds.has(item.id))
          .map((item) => {
            if (!item.subMenus?.length) return item;

            // Keep sub-menus that are explicitly listed,
            // OR keep all of them when only the parent id is listed (most common case)
            const hasGranularSubAccess = item.subMenus.some((s) =>
              allowedIds.has(s.id),
            );

            return {
              ...item,
              subMenus: hasGranularSubAccess
                ? item.subMenus.filter((s) => allowedIds.has(s.id))
                : item.subMenus, // parent allowed → all sub-menus visible
            };
          });
      },
    [allowedIds],
  );

  return {
    filterMenuItems,
    isItemAccessible,
    isPrivileged: false,
    roleName: "",
    firstAccessiblePath,
  };
};

export default useSidebarPermissions;
